import { useEffect, useState, useCallback } from "react";
import { Mailbox, Plus, Pencil, Power, X, Search } from "lucide-react";
import { adminApi } from "../api";

const EMPTY = {
  id_definicao: null,
  dimensao: "produto", // produto | pack | tipo_produto | tipo_pack
  valor: "",
  id_mailinglist: "",
  enviar_na_data_pagamento: true,
  enviar_na_data_fim_curso: false,
  ativo: true,
};

function dimensaoOf(m) {
  if (m.ID_Produto) return { tipo: "produto", valor: String(m.ID_Produto), label: `Produto #${m.ID_Produto}`, desc: m.ProdutoDescricao };
  if (m.ID_Pack) return { tipo: "pack", valor: String(m.ID_Pack), label: `Pack #${m.ID_Pack}`, desc: m.PackDescricao };
  if (m.ID_TipoProduto) return { tipo: "tipo_produto", valor: m.ID_TipoProduto, label: `Tipo Produto ${m.ID_TipoProduto}`, desc: m.TipoProdutoDescricao };
  if (m.ID_TipoPack) return { tipo: "tipo_pack", valor: m.ID_TipoPack, label: `Tipo Pack ${m.ID_TipoPack}`, desc: m.TipoPackDescricao };
  return { tipo: "?", valor: "", label: "—", desc: "" };
}

export default function AdminMoosendMaps() {
  const [maps, setMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await adminApi.moosendMapsList();
      setMaps(r.maps || []);
    } catch (e) {
      setError(e.message || "Erro ao carregar associações.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openNew() { setForm(EMPTY); setModalOpen(true); }

  function openEdit(m) {
    const d = dimensaoOf(m);
    setForm({
      id_definicao: m.ID_Definicao,
      dimensao: d.tipo,
      valor: d.valor,
      id_mailinglist: m.ID_MailingList_Moosend || "",
      enviar_na_data_pagamento: !!m.EnviarNaDataPagamento,
      enviar_na_data_fim_curso: !!m.EnviarNaDataFimCurso,
      ativo: !!m.Ativo,
    });
    setModalOpen(true);
  }

  async function save() {
    setSaving(true); setError("");
    const body = {
      id_produto: form.dimensao === "produto" ? Number(form.valor) || null : null,
      id_pack: form.dimensao === "pack" ? Number(form.valor) || null : null,
      id_tipo_produto: form.dimensao === "tipo_produto" ? String(form.valor).trim() : null,
      id_tipo_pack: form.dimensao === "tipo_pack" ? String(form.valor).trim() : null,
      id_mailinglist: form.id_mailinglist.trim(),
      enviar_na_data_pagamento: form.enviar_na_data_pagamento,
      enviar_na_data_fim_curso: form.enviar_na_data_fim_curso,
      ativo: form.ativo,
    };
    try {
      if (form.id_definicao) await adminApi.moosendMapUpdate(form.id_definicao, body);
      else await adminApi.moosendMapCreate(body);
      setModalOpen(false);
      await load();
    } catch (e) {
      setError(e.message || "Erro ao gravar.");
    } finally {
      setSaving(false);
    }
  }

  async function disable(m) {
    if (!window.confirm(`Desativar a associação #${m.ID_Definicao}? (reversível — fica Ativo=0)`)) return;
    try { await adminApi.moosendMapDelete(m.ID_Definicao); await load(); }
    catch (e) { setError(e.message || "Erro ao desativar."); }
  }

  const q = search.trim().toLowerCase();
  const filtered = maps.filter((m) => {
    if (!q) return true;
    const d = dimensaoOf(m);
    return [d.label, d.desc, m.ID_MailingList_Moosend, String(m.ID_Definicao)]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
  });

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="admin-moosend-maps">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#0F1829] flex items-center justify-center">
            <Mailbox className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-[#0F1829]">Produtos → Mailing List Moosend</h1>
            <p className="text-sm text-[#6B7280]">Liga um produto, pack ou tipo à mailing list que activa a automação ao pagar a encomenda.</p>
          </div>
        </div>
        <button onClick={openNew} data-testid="moosend-map-new-btn"
          className="inline-flex items-center gap-2 bg-[#0F1829] text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity">
          <Plus className="w-4 h-4" /> Nova associação
        </button>
      </div>

      <div className="relative mb-4">
        <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={search} onChange={(e) => setSearch(e.target.value)}
          data-testid="moosend-map-search"
          placeholder="Procurar por produto, descrição ou ID da lista…"
          className="w-full pl-9 pr-3 py-2 border border-[#E5E7EB] rounded-lg text-sm" />
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 text-red-700 border border-red-200 rounded text-sm" data-testid="moosend-map-error">{error}</div>}

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F9FAFB] text-[#6B7280] text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-3">#</th>
              <th className="text-left px-4 py-3">Produto / Pack / Tipo</th>
              <th className="text-left px-4 py-3">Mailing List (ID Moosend)</th>
              <th className="text-center px-4 py-3">Ao pagar</th>
              <th className="text-center px-4 py-3">Ao fim do curso</th>
              <th className="text-center px-4 py-3">Ativo</th>
              <th className="text-right px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody data-testid="moosend-map-rows">
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[#9CA3AF]">A carregar…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[#9CA3AF]">Sem associações.</td></tr>
            ) : filtered.map((m) => {
              const d = dimensaoOf(m);
              return (
                <tr key={m.ID_Definicao} className={`border-t border-[#F3F4F6] ${m.Ativo ? "" : "opacity-50"}`}>
                  <td className="px-4 py-3 text-[#6B7280]">{m.ID_Definicao}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#0F1829]">{d.label}</div>
                    {d.desc && <div className="text-xs text-[#6B7280]">{d.desc}</div>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-[#374151]">{m.ID_MailingList_Moosend}</td>
                  <td className="px-4 py-3 text-center">{m.EnviarNaDataPagamento ? "✓" : "—"}</td>
                  <td className="px-4 py-3 text-center">{m.EnviarNaDataFimCurso ? "✓" : "—"}</td>
                  <td className="px-4 py-3 text-center">{m.Ativo ? "Sim" : "Não"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(m)} data-testid={`moosend-map-edit-${m.ID_Definicao}`}
                      className="inline-flex items-center gap-1 text-[#0F1829] hover:underline text-xs mr-3">
                      <Pencil className="w-3.5 h-3.5" /> Editar
                    </button>
                    {m.Ativo && (
                      <button onClick={() => disable(m)} data-testid={`moosend-map-disable-${m.ID_Definicao}`}
                        className="inline-flex items-center gap-1 text-red-600 hover:underline text-xs">
                        <Power className="w-3.5 h-3.5" /> Desativar
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="moosend-map-modal">
          <div className="bg-white rounded-xl w-full max-w-lg shadow-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB]">
              <h2 className="font-semibold text-[#0F1829]">{form.id_definicao ? `Editar associação #${form.id_definicao}` : "Nova associação"}</h2>
              <button onClick={() => setModalOpen(false)} className="text-[#9CA3AF] hover:text-[#0F1829]"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Dimensão de associação</label>
                <select value={form.dimensao} onChange={(e) => setForm({ ...form, dimensao: e.target.value, valor: "" })}
                  data-testid="moosend-map-dimensao"
                  className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm">
                  <option value="produto">Produto (ID_Produto)</option>
                  <option value="pack">Pack (ID_Pack)</option>
                  <option value="tipo_produto">Tipo de Produto (ex.: SV, LI, LA)</option>
                  <option value="tipo_pack">Tipo de Pack</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">
                  {form.dimensao.startsWith("tipo") ? "Código do tipo" : "ID"}
                </label>
                <input value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })}
                  data-testid="moosend-map-valor"
                  placeholder={form.dimensao === "produto" ? "ex.: 236" : form.dimensao.startsWith("tipo") ? "ex.: SV" : "ex.: 848"}
                  className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Mailing List (ID Moosend)</label>
                <input value={form.id_mailinglist} onChange={(e) => setForm({ ...form, id_mailinglist: e.target.value })}
                  data-testid="moosend-map-list"
                  placeholder="ex.: 1ce6785c-1195-4031-a8f3-e7bf028f000e"
                  className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm font-mono" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.enviar_na_data_pagamento}
                    data-testid="moosend-map-pagamento"
                    onChange={(e) => setForm({ ...form, enviar_na_data_pagamento: e.target.checked })} />
                  Subscrever ao pagar a encomenda
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.enviar_na_data_fim_curso}
                    data-testid="moosend-map-fimcurso"
                    onChange={(e) => setForm({ ...form, enviar_na_data_fim_curso: e.target.checked })} />
                  Subscrever no fim do curso
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.ativo}
                    data-testid="moosend-map-ativo"
                    onChange={(e) => setForm({ ...form, ativo: e.target.checked })} />
                  Ativo
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-[#E5E7EB]">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 text-sm text-[#6B7280]">Cancelar</button>
              <button onClick={save} disabled={saving} data-testid="moosend-map-save-btn"
                className="bg-[#0F1829] text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50">
                {saving ? "A gravar…" : "Gravar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
